import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { UserCheck, RotateCcw, CheckCircle, Archive, Info } from 'lucide-react';
import { statusLabels, severityOptions, resultOptions } from './helpers';
import CloseReportDialog from './CloseReportDialog';
import ReopenReportDialog from './ReopenReportDialog';

// Roles allowed to appear in the "Atribuir a" dropdown. Reporters, external
// admins and the Cogniseg global user are intentionally excluded.
const ASSIGNABLE_ROLES = new Set(['case_manager', 'compliance_officer', 'triage_manager']);
const ROLE_LABELS = {
  case_manager: 'Gestor de Denúncias',
  compliance_officer: 'Compliance Officer',
  triage_manager: 'Gestor de Triagem',
};
const UNASSIGNED_VALUE = '__unassigned__';

export const CaseManagementCard = ({
  report,
  updating,
  onUpdate,
  users = [],
  canAssign = false,
}) => {
  const assignableUsers = users.filter(
    (u) => ASSIGNABLE_ROLES.has(u.role) && u.is_active !== false && !u.cogniseg_managed,
  );
  const currentAssignee = assignableUsers.find((u) => u.id === report.assigned_to);
  const showStaleWarning = report.assigned_to && !currentAssignee;

  // Reassignment reason dialog: shown when the caller tries to move an
  // in-progress report (status != RECEBIDA) from user A to user B. First
  // assignments and unassignments skip this dialog.
  const [reasonDialog, setReasonDialog] = useState({ open: false, newAssignee: null, reason: '' });

  const handleAssigneeChange = (value) => {
    const nextAssignee = value === UNASSIGNED_VALUE ? null : value;
    const isReassignment =
      report.assigned_to && nextAssignee && report.assigned_to !== nextAssignee;
    const inProgress = report.status && report.status !== 'RECEBIDA';
    if (isReassignment && inProgress) {
      setReasonDialog({ open: true, newAssignee: nextAssignee, reason: '' });
      return;
    }
    onUpdate('assigned_to', nextAssignee);
  };

  const confirmReassignment = () => {
    if (!reasonDialog.reason.trim()) return;
    onUpdate('assigned_to', reasonDialog.newAssignee, { reassignment_reason: reasonDialog.reason.trim() });
    setReasonDialog({ open: false, newAssignee: null, reason: '' });
  };

  // Close / reopen dialog state
  const [closeDialog, setCloseDialog] = useState({ open: false, defaultStatus: 'CONCLUIDA' });
  const [reopenOpen, setReopenOpen] = useState(false);
  const isClosed = ['CONCLUIDA', 'ARQUIVADA'].includes(report.status);

  const handleStatusChange = (nextStatus) => {
    if (nextStatus === report.status) return;
    // Closing requires the dedicated dialog (mandatory closure_message)
    if (['CONCLUIDA', 'ARQUIVADA'].includes(nextStatus)) {
      setCloseDialog({ open: true, defaultStatus: nextStatus });
      return;
    }
    // If report is currently closed, force use of the Reopen dialog
    if (isClosed) {
      setReopenOpen(true);
      return;
    }
    onUpdate('status', nextStatus);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Gestão do Caso</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label className="text-sm">Estado</Label>
          <Select
            value={report.status}
            onValueChange={handleStatusChange}
            disabled={updating || isClosed}
          >
            <SelectTrigger data-testid="status-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(statusLabels).map(([key, label]) => (
                <SelectItem key={key} value={key}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {isClosed && (
            <div className="mt-2 space-y-2">
              <Alert className="bg-slate-50 border-slate-200">
                <Info className="w-4 h-4 text-slate-500" />
                <AlertDescription className="text-xs text-slate-600">
                  Denúncia encerrada. Para retomar a investigação usa <strong>Reabrir denúncia</strong> — motivo é obrigatório.
                </AlertDescription>
              </Alert>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setReopenOpen(true)}
                className="w-full border-blue-200 text-blue-700 hover:bg-blue-50"
                data-testid="reopen-report-btn"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Reabrir denúncia
              </Button>
            </div>
          )}
          {!isClosed && (
            <div className="mt-2 flex flex-wrap gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setCloseDialog({ open: true, defaultStatus: 'CONCLUIDA' })}
                className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                data-testid="quick-conclude-btn"
              >
                <CheckCircle className="w-4 h-4 mr-1" />
                Concluir
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setCloseDialog({ open: true, defaultStatus: 'ARQUIVADA' })}
                className="border-slate-300 text-slate-700 hover:bg-slate-100"
                data-testid="quick-archive-btn"
              >
                <Archive className="w-4 h-4 mr-1" />
                Arquivar
              </Button>
            </div>
          )}
        </div>

        <div>
          <Label className="text-sm">Gravidade</Label>
          <Select
            value={report.severity || ''}
            onValueChange={(v) => onUpdate('severity', v)}
            disabled={updating}
          >
            <SelectTrigger data-testid="severity-select">
              <SelectValue placeholder="Selecionar..." />
            </SelectTrigger>
            <SelectContent>
              {severityOptions.map((sev) => (
                <SelectItem key={sev} value={sev}>{sev}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {canAssign && (
          <div>
            <Label className="text-sm flex items-center gap-1.5">
              <UserCheck className="w-3.5 h-3.5" /> Atribuído a
            </Label>
            <Select
              value={report.assigned_to || UNASSIGNED_VALUE}
              onValueChange={handleAssigneeChange}
              disabled={updating}
            >
              <SelectTrigger data-testid="assignee-select">
                <SelectValue placeholder="Não atribuído">
                  {currentAssignee
                    ? `${currentAssignee.name} · ${ROLE_LABELS[currentAssignee.role] || currentAssignee.role}`
                    : 'Não atribuído'}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={UNASSIGNED_VALUE}>Não atribuído</SelectItem>
                {assignableUsers.length === 0 && (
                  <SelectItem value="__no_users__" disabled>
                    Sem utilizadores atribuíveis
                  </SelectItem>
                )}
                {assignableUsers.map((u) => (
                  <SelectItem key={u.id} value={u.id}>
                    {u.name} · {ROLE_LABELS[u.role] || u.role}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {showStaleWarning && (
              <p className="text-xs text-amber-600 mt-1">
                Atribuição atual aponta para um utilizador que já não existe ou foi desativado.
              </p>
            )}
          </div>
        )}

        {['CONCLUIDA', 'PENDENTE_APROVACAO'].includes(report.status) && (
          <div>
            <Label className="text-sm">Resultado</Label>
            <Select
              value={report.result || ''}
              onValueChange={(v) => onUpdate('result', v)}
              disabled={updating}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecionar resultado..." />
              </SelectTrigger>
              <SelectContent>
                {resultOptions.map((res) => (
                  <SelectItem key={res} value={res}>
                    {res.replace('_', ' ')}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </CardContent>

      <Dialog open={reasonDialog.open} onOpenChange={(o) => !o && setReasonDialog({ open: false, newAssignee: null, reason: '' })}>
        <DialogContent data-testid="reassign-reason-dialog">
          <DialogHeader>
            <DialogTitle>Motivo da reatribuição</DialogTitle>
            <DialogDescription>
              Esta denúncia já está em curso. Descreva porque está a transferir a
              atribuição para outro gestor — este motivo fica registado no histórico do caso.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            rows={4}
            value={reasonDialog.reason}
            onChange={(e) => setReasonDialog(prev => ({ ...prev, reason: e.target.value }))}
            placeholder="Ex.: Gestor anterior indisponível durante 2 semanas; caso urgente redistribuído para..."
            data-testid="reassign-reason-input"
          />
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setReasonDialog({ open: false, newAssignee: null, reason: '' })}
              data-testid="reassign-reason-cancel"
            >
              Cancelar
            </Button>
            <Button
              onClick={confirmReassignment}
              disabled={!reasonDialog.reason.trim()}
              className="bg-indigo-700 hover:bg-indigo-800 text-white"
              data-testid="reassign-reason-confirm"
            >
              Reatribuir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CloseReportDialog
        open={closeDialog.open}
        onOpenChange={(o) => setCloseDialog((s) => ({ ...s, open: o }))}
        report={report}
        defaultStatus={closeDialog.defaultStatus}
        onClosed={() => onUpdate('__refetch__')}
      />

      <ReopenReportDialog
        open={reopenOpen}
        onOpenChange={setReopenOpen}
        report={report}
        onReopened={() => onUpdate('__refetch__')}
      />
    </Card>
  );
};
